import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { suggestedFollowUps } from "@omina/core";

import { LoopHero } from "@/components/home/LoopHero";
import { Upcoming, type UpcomingRoom } from "@/components/home/Upcoming";
import { RoomBriefCard } from "@/components/home/RoomBriefCard";
import { WaitingOnYou } from "@/components/home/WaitingOnYou";
import { WhatsNew } from "@/components/home/WhatsNew";
import {
  useContacts,
  useCurationQueue,
  useEventRadar,
  useLoopSummary,
  useMe,
  useMoves,
  usePickedEvents,
  useRoomBrief,
  useSettings,
} from "@/lib/queries";

/**
 * Today — what to do, in the order it matters.
 *
 * Four things, and the order is the argument:
 *
 *   1. the loop's health, as one number   → LoopHero
 *   2. what is outstanding                → WaitingOnYou
 *   3. what is coming                     → Upcoming
 *   4. what else is out there             → WhatsNew
 *
 * Debt before diary on purpose. A founder opening this app has usually just
 * *been* somewhere, and the thing that decays is the follow-up she has not
 * sent — the room she is going to on Thursday will still be there after she
 * has written to the person she met on Monday.
 *
 * **This page holds no lists that another screen owns.** Not the feed's rooms,
 * not the circle's contacts. Each section is a count and a way in, so nothing
 * here can go stale against the screen that actually owns it — the failure
 * that made "happening near you" pointless on the radar.
 */
export const Route = createFileRoute("/home")({
  component: Home,
});

function Home() {
  const me = useMe();
  const settings = useSettings();
  const summary = useLoopSummary();
  const contacts = useContacts();
  const moves = useMoves();
  const picked = usePickedEvents(30);
  const curation = useCurationQueue();
  const goal = settings.data?.activeGoal;
  const radar = useEventRadar(goal, 45, Boolean(goal));

  const firstName = me.data?.user.name?.split(" ")[0];

  /**
   * Met, and followed up.
   *
   * `byState.new` is the count with no move against them, so everyone else has
   * had something sent. Derived from the same summary the rest of the loop
   * reads rather than recounted here — two ways of computing "followed up"
   * would disagree the first time one of them changed.
   */
  const met = summary.data?.counts.contacts ?? 0;
  const notWritten = summary.data?.byState.new ?? 0;
  const followedUp = Math.max(0, met - notWritten);

  /** Only rooms still ahead — a past room is curation's problem, not the
   *  diary's. */
  const upcoming: UpcomingRoom[] = useMemo(() => {
    const now = Date.now();
    return (picked.data ?? [])
      .filter((p) => new Date(p.startsAt || p.date).getTime() >= now)
      .sort(
        (a, b) =>
          new Date(a.startsAt || a.date).getTime() -
          new Date(b.startsAt || b.date).getTime(),
      )
      .slice(0, 3)
      .map((p) => ({
        id: p.id,
        name: p.name,
        date: p.date,
        startsAt: p.startsAt,
        location: p.location,
        url: p.url,
      }));
  }, [picked.data]);

  const brief = useRoomBrief();

  const peopleWaiting = useMemo(() => {
    if (!contacts.data || !moves.data) return 0;
    return suggestedFollowUps(contacts.data, moves.data, dayKey(new Date())).length;
  }, [contacts.data, moves.data]);

  return (
    <div className="homewrap">
      <LoopHero met={met} followedUp={followedUp} name={firstName} />

      <div className="homebody">
        <WaitingOnYou
          peopleWaiting={peopleWaiting}
          roomsToJudge={curation.data?.pending ?? 0}
        />

        <Upcoming rooms={upcoming} />

        {/* Sits under Upcoming because it is about those same rooms — what is
            coming, then who is in it. Silent when nothing is readable. */}
        <RoomBriefCard readable={brief.data?.readable ?? 0} />

        <WhatsNew count={radar.data?.candidates.length ?? 0} />
      </div>
    </div>
  );
}

/** Local YYYY-MM-DD. `toISOString` would shift the day for anyone west of
 *  UTC, which is most of this product's users. */
function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
